import { ChangeEvent, useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraHStack,
  ChakraSimpleGrid,
  ChakraText,
  CircularProgress,
  EquipUITheme,
  TextInput,
} from '@equip.health/ui';

import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useAddObservationMutation } from '~/store/services/clinicalData';

import ActionButton from './ActionButton';
import DatetimeGridItem from './DatetimeGridItem';
import GoalStatusGridItem from './GoalStatusGridItem';
import {
  DateSourceColSpan,
  EditStatusColSpan,
  GoalStatusColSpan,
  MeasuredAtColSpan,
} from './utils';
import ValueGridItem from './ValueGridItem';
import {
  ObservationStatusType,
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
  keyVitalsItemStyles,
} from '../../constants';

const TOP_LEVEL_ID = 'patient-vitals__add-value';

const { label: labelStyle, paragraph } = EquipUITheme.typography;

type NewKeyVitalsItemProps = {
  finishAddingValue: () => void;
  patientId: string;
  tab: PatientVitalsTabs;
};

const NewKeyVitalsItem = ({
  finishAddingValue,
  patientId,
  tab,
}: NewKeyVitalsItemProps) => {
  const [value, setValue] = useState<string>();
  const [interpretation, setInterpretation] = useState<string>();
  const [datetime, setDatetime] = useState<Date>(new Date());
  const [noteValue, setNoteValue] = useState<string>();

  const { label, unit } = PatientVitalsTabsCollection[tab] ?? {};

  const { currentUser } = useGlobalContext();

  const [
    saveObservation,
    {
      isError: saveObservationError,
      isLoading: isSaveObservationLoading,
      isSuccess: isSaveObservationSuccess,
    },
  ] = useAddObservationMutation();

  const handleSave = () => {
    const commonOptions = {
      createdBy: `${currentUser.firstName} ${currentUser.lastName}`,
      interpretation,
      note: noteValue,
      observationDatetime: datetime.toISOString(),
      secondaryValue: null,
      source: 'Manual',
      status: ObservationStatusType.FINAL,
      updatedByUserId: currentUser.userId,
    };

    const observationOptions = [];
    // Multiple observations

    const options = {
      ...commonOptions,
      type: label,
      unit,
      updatedByUserId: currentUser.userId,
      value: Number(value),
    };

    observationOptions.push(options);

    // Save single/multiple observations
    observationOptions.forEach((option) => {
      saveObservation({ ...option, patientId });
    });
  };

  useEffect(() => {
    if (isSaveObservationSuccess) {
      finishAddingValue();
    }
  }, [isSaveObservationSuccess]);

  return (
    <ChakraBox {...keyVitalsItemStyles.container} id={TOP_LEVEL_ID}>
      <ChakraSimpleGrid
        {...keyVitalsItemStyles.mainGrid}
        templateColumns="repeat(13, 1fr)"
      >
        <ValueGridItem<string>
          colSpan={2}
          id={`${TOP_LEVEL_ID}__single-value`}
          isEditing
          onChange={(val) => setValue(val)}
          tab={tab}
          unit={unit as string}
          value={value}
        />
        <DatetimeGridItem
          colSpan={MeasuredAtColSpan}
          id={`${TOP_LEVEL_ID}__measured-at`}
          isEditing
          onChange={(val: Date) => setDatetime(val)}
          value={datetime}
        />
        <ChakraGridItem alignItems="center" colSpan={DateSourceColSpan}>
          <ChakraText {...paragraph} id={`${TOP_LEVEL_ID}__source`}>
            Manual
          </ChakraText>
        </ChakraGridItem>
        <GoalStatusGridItem
          colSpan={GoalStatusColSpan}
          id={`${TOP_LEVEL_ID}__goal-status`}
          isEditing
          onChange={(val) => setInterpretation(val)}
          value={interpretation}
        />
        <ChakraGridItem colSpan={EditStatusColSpan}>
          <ChakraFlex justify="flex-end">
            {isSaveObservationLoading ? (
              <CircularProgress
                id={`${TOP_LEVEL_ID}__loading`}
                isIndeterminate
                size="20px"
              />
            ) : (
              <ChakraHStack spacing="4px">
                <ActionButton
                  id={`${TOP_LEVEL_ID}__save`}
                  isDisabled={!datetime || value === null}
                  onClick={handleSave}
                  title="Save"
                  variant="solid"
                />
                <ActionButton
                  id={`${TOP_LEVEL_ID}__cancel`}
                  onClick={finishAddingValue}
                  title="Cancel"
                  variant="outline"
                />
              </ChakraHStack>
            )}
          </ChakraFlex>
        </ChakraGridItem>
      </ChakraSimpleGrid>
      <ChakraSimpleGrid
        gap="24px"
        id={`${TOP_LEVEL_ID}__note-grid`}
        paddingTop="16px"
      >
        <ChakraText
          {...labelStyle}
          id={`${TOP_LEVEL_ID}__note-label`}
          lineHeight="120%"
        >
          Note
        </ChakraText>
        <TextInput
          color="neutral.primary"
          formMarginBottom="10px"
          height="32px"
          id={`${TOP_LEVEL_ID}__note-input`}
          isRequired
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setNoteValue(event.target.value);
          }}
          size="sm"
          value={noteValue}
        />
      </ChakraSimpleGrid>
      {saveObservationError && (
        <ChakraText {...labelStyle} color="critical.base" textAlign="right">
          Error saving observation
        </ChakraText>
      )}
    </ChakraBox>
  );
};

export default NewKeyVitalsItem;
